import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction
} from 'react';
import { SubscriptionPackagesType } from '../types/types';

export interface User {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  phone?: string;
  phoneVerified?: boolean;
  country?: string;
  state?: string;
  username: string | null;
  walletAddress: string;
  referralCode: string;
  email: string;
  tokenAmount: string;
  entries: number;
  stackingEntries?: number;
  // Entries in 10k draws closed but not yet spun (a postponed draw) — see utils/pendingDraws.
  pendingJackpot?: { endDate: string; entries: number }[];
  nextFaucetClaim: string;
  hasActiveSubscription: boolean;
  hasTwoFactorAuthEnabled: boolean;
  showTwoFactorAuth: boolean;
  showReferral: boolean;
  refreshImage: string;
  discordName: string;
  isGiveawayEligible: boolean | null;
  hasClaimed5000Airdrop: boolean;
  role: string;
  userHasHotWallet: boolean;
  earnedTokens: string;
  // ISO time the 48-hour hold after a 2FA reset lifts; null or absent when none (utils/securityHold.ts)
  securityHoldUntil?: string | null;
}

export interface UserSubscriptionContextData {
  package: SubscriptionPackagesType | null;
  endDate: string | null;
}

interface UserContextType {
  user: User;
  setUser: Dispatch<SetStateAction<User>>;
  userCurrentSubscription: UserSubscriptionContextData;
  setUserCurrentSubscription: (value: UserSubscriptionContextData) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>(() => {
    const storedUser = localStorage.getItem('incrypt_app_userData');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [userCurrentSubscription, setUserCurrentSubscription] =
    useState<UserSubscriptionContextData>({
      package: null,
      endDate: null
    });

  useEffect(() => {
    if (user) {
      localStorage.setItem('incrypt_app_userData', JSON.stringify(user));
    } else {
      localStorage.removeItem('incrypt_app_userData');
    }
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userCurrentSubscription,
        setUserCurrentSubscription
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
